'use client';

import { useEffect } from 'react';

interface DynamicExternalScriptLoaderProps {
  scriptUrl: string;
  async?: boolean;
  defer?: boolean;
  id?: string;
}

const DynamicExternalScriptLoader = ({
  scriptUrl,
  async = true,
  defer = true,
  id,
}: DynamicExternalScriptLoaderProps) => {
  useEffect(() => {
    // Skip if no script URL is provided
    if (!scriptUrl) {
      return;
    }

    // Check if the script is already loaded
    const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);
    if (existingScript) {
      console.log(`Script ${scriptUrl} is already loaded`);
      return;
    }

    try {
      console.log(`Loading external script: ${scriptUrl}`);

      // Create script element
      const script = document.createElement('script');
      script.src = scriptUrl;
      script.async = async;
      script.defer = defer;

      // Add ID if provided
      if (id) {
        script.id = id;
      }

      // Add error handling
      script.onerror = () => {
        console.error(`Failed to load script: ${scriptUrl}`);
      };

      // Add to the document
      document.body.appendChild(script);

      // Cleanup function to remove the script when component unmounts
      return () => {
        const scriptToRemove = document.querySelector(
          `script[src="${scriptUrl}"]`
        );
        if (scriptToRemove && scriptToRemove.parentNode) {
          scriptToRemove.parentNode.removeChild(scriptToRemove);
        }
      };
    } catch (error) {
      console.error('Error loading external script:', error);
    }
  }, [scriptUrl, async, defer, id]);

  // This component doesn't render anything visible
  return null;
};

export default DynamicExternalScriptLoader;
